'use strict';
/* ==========================================================================
   levels/rap.js — step 3, ENERGY DON'T QUIT: the lyric music video (DESIGN.md §6).

   ► The song: assets/music/rap/top1.m4a (ACE-Step take funk_21, the chosen and only take).

   The whole frame is a pure function of SONG TIME t (the Web Audio clock of the playing
   buffer; a wall clock while audio is locked or missing), so seeking, pausing and the QA
   screenshots are exact. Timing data: assets/music/rap/top1_words.json (tools/rap_whisper.py
   + tools/rap_timing.py): every lyric line of content/rap.json with word times, the hook
   repeats / ad-libs the singer added (`extras`), and the beat grid {bpm, offset = first downbeat}.

   Four acts, keyed to lyric-line indices (so the acts follow the take's own timings):
     ride      intro + verse 1 + chorus 1 (lines 0–13): the chain lift runs under the spoken intro
               and parks the cart on the crest for "parked up at the top"; the drop hits on "then the
               drop hits" and bottoms out on "kinetic"; hills and a loop through chorus 1, the energy
               chart swapping PE ↔ KE while the TOTAL stays in its dashed outline.
     race      the elephant bars (14–19): Max (50 kg) vs the elephant (5000 kg) on two lanes, a tie
               on "bottom", m g h = ½ m v² with the m's crossed off, then v = √(2 g h).
     friction  the friction bars (20–23): hills 20 / 15 / 11 / 8 m with rolling friction, warm
               wheels, the HEAT bar growing, crest heights tagged on "first hill is always the tallest".
     finale    chorus 2 + outro (24–end): Voss and Max nod on the beat, the loop coaster leaves the
               crest on a downbeat every four bars, cut-outs pulse on downbeats, CLASS DISMISSED.
   Every cart comes from a Coaster.Sim (levels/rap_tracks.js). Karaoke (bottom third): the current
   line in big handwriting, sung words written on, the active word on a mustard scrap, the next line
   faint below; hook words and formulas pop as ransom letters (keepCase) on their word times.
   Controls: click / Space play-pause, ↺ restart (R), ⌂ map, ← → seek 5 s, click or DRAG the progress strip
   (the on-screen seek for touch screens; its hit zone is ≥ 44 CSS px tall there, docs/WEBSITE.md §8.2).
   The first full listen → Game.complete('rap').
   The song is long music (docs/WEBSITE.md §8.4): lesson.json preload.stream names it, so it is kept compressed and
   played through the shared <audio> element (Audio.media), whose time is the clock — never decoded into memory.
   A tab switch pauses it (core/device.js); the PAUSED card's tap resumes it (inside that tap: iOS allows play()).
   ========================================================================== */
(function () {
  const TAKE = 'top1', TAKE_NAME = 'funk_21';     // the song: music/rap/top1.m4a + top1_words.json = ACE-Step take funk_21
  const DIR = 'music/rap/';   // logical, resolved through Assets.url (shared engine core/assets.js)
  const HAND = "'Caveat Brush'";
  const GROUND = 800;                             // screen y of the ground line in every act
  const BAND_Y = 832;                             // top of the lyric band
  const CUR_Y = 912, NEXT_Y = 1012, CUR_SIZE = 74, NEXT_SIZE = 44;
  const OUTLINE = 'rgba(37,60,90,0.92)';
  const WIPE = 0.6;                               // torn-paper wipe between acts (s)

  /* ---------------- song timing ---------------- */
  const normW = w => String(w).toLowerCase().replace(/[^a-z0-9½²√]/g, '');
  class Song {
    constructor(d) {
      this.d = d;
      this.lines = d.lines;
      this.dur = d.duration || 105;
      const b = d.beats || {};
      this.bpm = b.bpm || 94; this.bt = 60 / this.bpm; this.off = b.offset || 0;
      // what the karaoke shows, in time order: lyric lines + the singer's extra hook repeats / ad-libs
      this.items = d.lines.map(l => ({ ...l, kind: 'line' }))
        .concat((d.extras || []).map((x, j) => ({ ...x, i: 100 + j })))
        .sort((a, b) => a.s - b.s);
    }
    S(i) { return this.lines[i] ? this.lines[i].s : 1e6; }
    E(i) { return this.lines[i] ? this.lines[i].e : 1e6; }
    /* start of the n-th word of line i that matches `word` (exact, then prefix); the line start if none */
    W(i, word, n = 1) {
      const l = this.lines[i]; if (!l) return 1e6;
      const k = normW(word);
      for (const pass of [w => normW(w.w) === k, w => normW(w.w).startsWith(k)]) {
        let c = 0;
        for (const w of l.words) if (pass(w) && ++c === n) return w.s;
      }
      return l.s;
    }
    /* beat grid */
    beatPos(t) { return (t - this.off) / this.bt; }
    sinceBeat(t) { const p = this.beatPos(t); return (p - Math.floor(p)) * this.bt; }
    sinceDown(t) { const p = this.beatPos(t) / 4; return (p - Math.floor(p)) * this.bt * 4; }
    pulse(t, k = 9) { return t < this.off - 0.01 ? 0 : Math.exp(-this.sinceBeat(t) * k); }
    downPulse(t, k = 7) { return t < this.off - 0.01 ? 0 : Math.exp(-this.sinceDown(t) * k); }
    nextDown(t) { const bar = this.bt * 4; return this.off + Math.ceil((t - this.off) / bar - 1e-6) * bar; }
  }

  /* ---------------- the acts' timing (per take) ---------------- */
  function plan(song) {
    const S = song, p = {};
    p.bRace = S.S(14) - 0.25;                     // act boundaries
    p.bFric = S.S(20) - 0.25;
    p.bFinale = S.S(24) - 0.25;
    // ride: the drop bottoms out on "kinetic" (line 7); the lift parks on the crest just before
    const probe = RapTracks.get('ride', { tPark: 12 });
    p.tRide0 = S.W(7, 'kinetic') - probe.marks.tBottom;
    p.tPark = Math.max(4, p.tRide0 - 0.25);
    p.ride = RapTracks.get('ride', { tPark: p.tPark });
    // race: both carts reach the bottom on "bottom" (line 16), released after the elephant is in
    p.race = RapTracks.get('race');
    p.tRace0 = Math.max(S.W(15, 'elephant') + 0.6, S.W(16, 'bottom') - p.race.marks.tBottomMax);
    p.tTie = p.tRace0 + p.race.marks.tBottomMax;
    // friction: released so the second crest comes on "heat" (line 21)
    p.fric = RapTracks.get('friction');
    const tCrest2 = p.fric.sim.timeAtX(p.fric.marks.crests[1]);
    p.tFric0 = Math.max(p.bFric, S.W(21, 'heat') - tCrest2 + 0.3);
    // finale: the loop coaster leaves the crest on a downbeat, every 4 bars
    p.loop = RapTracks.get('loop');
    p.tLoop0 = S.nextDown(p.bFinale + 0.2);
    p.loopCycle = Math.max(4, Math.ceil((p.loop.sim.tSettle || 10) / (S.bt * 4))) * S.bt * 4;
    return p;
  }

  /* ---------------- small drawing helpers ---------------- */
  function sky(scroll = 0) {
    Levels.title.blueprint(9);
    const clouds = [[180, 150, 150, 46], [620, 250, 110, 34], [1150, 120, 170, 50], [1560, 260, 120, 36], [2050, 170, 140, 44], [2420, 90, 100, 30]];
    clouds.forEach(([x, y, rx, ry], i) => {
      const X = ((x - scroll * 0.18) % 2600 + 2600) % 2600 - 340;
      paperPiece(blobPts(rx, ry, 60 + i, 0.08, 30), X, y, PAL.cream, { seed: 60 + i, depth: 0.6, alpha: 0.5, boil: false });
    });
  }
  function groundStrip(scroll = 0) {
    const TW = 1240, o = ((scroll % TW) + TW) % TW, base = Math.floor(scroll / TW);
    for (let i = -1; i <= 2; i++) paperRect(i * TW - o + TW / 2, GROUND + 140, TW + 140, 290, PAL.kraft, { seed: 300 + (((i + base) % 7) + 7) % 7, torn: true, tex: 'tex_kraft', depth: 1.2, boil: false });
  }
  function label(str, x, y, size, t0, o = {}) { hand(str, x, y, size, t0, Object.assign({ color: PAL.cream, outline: OUTLINE, seed: 5 }, o)); }
  const fontFor = (str, size) => (/[a-z]/.test(str) ? `${Math.round(size * 0.92)}px 'Alfa Slab One'` : `${size}px 'Luckiest Guy'`);
  function tag(str, x, y, size, s = 1, o = {}) {
    if (s <= 0) return;
    g.save(); g.translate(x, y); g.rotate(o.rot ?? -0.05); g.scale(s, s);
    g.font = fontFor(str, size);
    const w = g.measureText(str).width + size * 0.8, h = size * 1.35;
    paperRect(0, 0, w, h, o.color || PAL.cream, { seed: o.seed || 41, depth: 1.1, outline: true });
    g.fillStyle = o.ink || PAL.ink; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(str, 0, size * 0.07);
    g.restore();
  }
  /* a formula card: cream paper + ransom tokens [{s: text, t0}] laid out left→right (token t0 = its word) */
  function formulaCard(toks, cx, cy, size, t0, o = {}) {
    const sc = pop(T, t0, 0.35, 2) * (o.exitT != null ? unpop(T, o.exitT, 0.25) : 1) * (o.s ?? 1);
    if (sc <= 0.01) return null;
    const gap = size * 0.4, seed = o.seed || 1;
    const widths = toks.map((tk, i) => ransom(tk.s, -9999, -9999, size, 1e9, { keepCase: true, seed: seed + i * 7 }));
    const total = widths.reduce((a, b) => a + b, 0) + gap * (toks.length - 1);
    const w = total + size * 1.3, h = size * 2.1;
    g.save(); g.translate(cx, cy); g.rotate(o.rot ?? -0.015); g.scale(sc, sc);
    paperRect(0, 0, w, h, o.color || PAL.cream, { seed, depth: 1.5 });
    tape(-w / 2 + 40, -h / 2 + 6, 110, -0.5, undefined, seed + 3);
    tape(w / 2 - 40, -h / 2 + 6, 110, 0.5, undefined, seed + 4);
    let x = -total / 2; const out = [];
    toks.forEach((tk, i) => {
      const c = x + widths[i] / 2;
      ransom(tk.s, c, 4, size, tk.t0, { keepCase: true, seed: seed + i * 7, stagger: 0.04 });
      out.push({ x: c, w: widths[i] }); x += widths[i] + gap;
    });
    g.restore();
    return { toks: out, w, h, sc };
  }
  function strike(x0, y0, x1, y1, prog, seed) {
    ink([[x0, y0], [(x0 + x1) / 2 + 4, (y0 + y1) / 2 - 3], [x1, y1]], { w: 10, color: PAL.red, seed, prog, passes: 2, step: 10 });
  }

  /* ---------------- karaoke ---------------- */
  const HOOK = new Set(['potential', 'kinetic', 'conservation', 'elephant', 'heat']);
  const GROUPS = [
    { words: ['one', 'half', 'm', 'v', 'squared'], text: '½ m v²' },
    { words: ['root', 'of', 'two', 'g', 'h'], text: '√(2 g h)' },
  ];
  const layoutCache = new Map();
  /* display tokens of a line: plain words (handwriting) or ransom (hook words / formula groups) */
  function layout(item, size) {
    const key = item.i + '|' + item.s + '|' + size;
    if (layoutCache.has(key)) return layoutCache.get(key);
    const ws = item.words, toks = [];
    for (let k = 0; k < ws.length; k++) {
      const grp = GROUPS.find(G => G.words.every((w, j) => ws[k + j] && normW(ws[k + j].w) === w));
      if (grp) {
        const last = ws[k + grp.words.length - 1];
        toks.push({ text: grp.text, s: ws[k].s, e: last.e, ransom: true, trail: /[,.]$/.test(last.w) ? last.w.slice(-1) : '' });
        k += grp.words.length - 1; continue;
      }
      const bare = ws[k].w.replace(/[,.!?]+$/, '');
      if (HOOK.has(normW(bare))) toks.push({ text: bare, s: ws[k].s, e: ws[k].e, ransom: true, trail: ws[k].w.slice(bare.length) });
      else toks.push({ text: ws[k].w, s: ws[k].s, e: ws[k].e });
    }
    g.save(); g.font = `${size}px ${HAND}`;
    const space = g.measureText(' ').width, rs = size * 0.62;
    let x = 0;
    toks.forEach((tk, j) => {
      tk.seed = 400 + item.i * 13 + j * 5;
      if (tk.ransom) {
        tk.w = ransom(tk.text, -9999, -9999, rs, 1e9, { keepCase: true, seed: tk.seed });
        g.font = `${size}px ${HAND}`;
        tk.tw = tk.trail ? g.measureText(tk.trail).width : 0;
        tk.x = x + size * 0.12; x += tk.w + tk.tw + size * 0.24 + space;
      } else {
        tk.w = g.measureText(tk.text).width; tk.x = x; x += tk.w + space;
      }
    });
    g.restore();
    const out = { toks, width: x - space, size, rs };
    layoutCache.set(key, out);
    return out;
  }
  /* the current line: faint preview → written on as it is sung → active word highlighted */
  function drawCurrent(item, t, y, maxW = 1780) {
    let size = item.kind === 'adlib' ? 54 : CUR_SIZE;
    let Lo = layout(item, size);
    if (Lo.width > maxW) { size = Math.floor(size * maxW / Lo.width); Lo = layout(item, size); }
    const x0 = 960 - Lo.width / 2;
    const appear = item.appear ?? item.s - 0.55, a = E.outCubic(P(t, appear, appear + 0.25));
    const yy = y + (1 - a) * 40;
    Lo.toks.forEach((tk, j) => {
      const nx = Lo.toks[j + 1], end = Math.min(Math.max(tk.e, tk.s + 0.18) + 0.05, nx ? nx.s - 0.02 : 1e9);
      const x = x0 + tk.x, sung = t >= tk.s, active = t >= tk.s - 0.02 && t < end;
      if (active && !tk.ransom) {
        const bounce = 1 + 0.06 * Math.exp(-(t - tk.s) * 10);
        paperRect(x + tk.w / 2, yy + size * 0.06, (tk.w + 22) * bounce, size * 0.92, PAL.mustard, { seed: tk.seed, depth: 0.5, boil: false, alpha: 0.95 });
      }
      if (tk.ransom) {
        if (!sung) hand(tk.text, x + tk.w / 2, yy, size * 0.8, -1e6, { dur: 0.01, alpha: 0.28 * a, seed: tk.seed, font: "'Special Elite'" });
        else {
          const beat = 1 + 0.05 * Math.exp(-(t - tk.s) * 6);
          g.save(); g.translate(x + tk.w / 2, yy); g.scale(beat, beat);
          ransom(tk.text, 0, 0, Lo.rs, tk.s, { keepCase: true, seed: tk.seed, stagger: Math.min(0.05, Math.max(0.02, (tk.e - tk.s) / Math.max(1, tk.text.length))) });
          g.restore();
        }
        if (tk.trail) hand(tk.trail, x + tk.w + 4, yy, size, sung ? -1e6 : 1e6, { dur: 0.01, align: 'left', seed: tk.seed + 1 });
        return;
      }
      if (!sung || active) hand(tk.text, x, yy, size, -1e6, { dur: 0.01, align: 'left', alpha: 0.3 * a, seed: tk.seed });
      if (!sung) return;
      hand(tk.text, x, yy, size, tk.s, { dur: clamp(tk.e - tk.s, 0.08, 0.3), align: 'left', color: active ? PAL.red : PAL.ink, seed: tk.seed });
    });
  }
  function drawNext(item, t, y) {
    let size = NEXT_SIZE;
    g.save(); g.font = `${size}px ${HAND}`; const w = g.measureText(item.text).width; g.restore();
    if (w > 1700) size = Math.floor(size * 1700 / w);
    hand(item.text, 960, y, size, -1e6, { dur: 0.01, alpha: 0.5, color: PAL.navy, seed: 90 + item.i });
  }
  /* which item is current / next at t */
  /* a line comes up 0.55 s before its first word, but never before the previous line has been sung */
  const appearAt = (it, k) => (k === 0 ? it[0].s - 0.55 : Math.min(it[k].s, Math.max(it[k].s - 0.55, it[k - 1].e + 0.05)));
  function lyricAt(song, t) {
    const it = song.items;
    let cur = -1;
    for (let k = 0; k < it.length; k++) if (t >= appearAt(it, k)) cur = k;
    let c = cur >= 0 ? it[cur] : null;
    if (c) c.appear = appearAt(it, cur);
    const nx = it[cur + 1] || null;
    if (c && t > c.e + 2.2 && (!nx || nx.s - t > 1.2)) c = null;       // long instrumental gap: clear the line
    return { cur: c, next: nx && nx.s - t < 8 ? nx : null };
  }
  function band(song, t) {
    paperRect(960, BAND_Y + 140, 2040, 290, PAL.cream, { seed: 77, torn: true, depth: 1.8, boil: false });
    tape(110, BAND_Y + 6, 150, -0.08, undefined, 71); tape(1810, BAND_Y + 6, 150, 0.08, undefined, 72);
    const { cur, next } = lyricAt(song, t);
    const sec = (cur || next || {}).section;
    if (sec) hand(sec.toLowerCase(), 70, BAND_Y + 34, 30, -1e6, { dur: 0.01, align: 'left', color: 'rgba(37,60,90,0.55)', seed: 73, font: "'Special Elite'" });
    if (cur) drawCurrent(cur, t, CUR_Y);
    if (next) drawNext(next, t, NEXT_Y);
    else if (song.items.length && t > song.items[song.items.length - 1].e) hand('(the song is AI-generated; the physics is real)', 960, NEXT_Y, 40, song.items[song.items.length - 1].e, { color: PAL.navy, alpha: 0.7, seed: 193 });
    if (!cur && !next && t > 1) {
      // instrumental: ink notes hopping on the beat
      const b = ((Math.floor(song.beatPos(t)) % 3) + 3) % 3, pu = song.pulse(t, 6);
      ['♪', '♫', '♪'].forEach((n, i) => hand(n, 860 + i * 100, CUR_Y - 26 * pu * (i === b ? 1 : 0.15), 80, -1e6, { dur: 0.01, color: PAL.navy, seed: 80 + i, font: "'Segoe UI Symbol', 'DejaVu Sans', sans-serif" }));
    }
  }

  /* ---------------- the cart + a seated rider ----------------
     Seated the way the video seats them (levels/video.js cartRider + VK.RIDERS, tuned for the new
     assets/img/cart.png and Coaster.CART_FIT): the rider stands in the cart's frame at the per-image
     seat offset and is CLIPPED at the seat line, so the cart body hides the legs whatever the cart
     art's transparent gaps (Coaster.drawCart's own rider option doesn't clip, and the old
     riderDy: 30 left Max's legs dangling under the new cart).
     o: { h, rider, scale (× the video's rider height, pops multiply in), glow, seed } → drawCart's pose */
  const RAP_RIDERS = (window.VK && VK.RIDERS) || {
    max_cross: { rh: 3.4, feet: 0.36, dx: -0.2, flip: true },
    elephant: { rh: 2.25, feet: 0.30, dx: 0.02, flip: true },
  };
  function rideCart(tr, vw, s, o) {
    const h = o.h, R_ = o.rider && RAP_RIDERS[o.rider], rh = R_ ? h * R_.rh * (o.scale ?? 1) : 0;
    if (R_ && rh > 1) {
      const p = tr.at(s), railW = Math.max(6, 0.55 * vw.k), d = IMG.cart ? Coaster.CART_FIT.dy * h : 0;
      const lift = h / 2 + railW / 2 - 2 - d, nx = -Math.sin(p.angle), ny = -Math.cos(p.angle);
      g.save(); g.translate(vw.X(p.x) + nx * lift, vw.Y(p.y) + ny * lift); g.rotate(-p.angle);
      g.beginPath(); g.rect(-rh, -rh * 3, rh * 2, rh * 3 + h * 0.02); g.clip();
      const fy = rh * R_.feet;
      if (IMG[o.rider]) cut(o.rider, R_.dx * h, fy, rh, { ax: 0.5, ay: 1, flip: R_.flip, depth: 0.8, seed: (o.seed ?? 5) + 3 });
      else { paperRect(R_.dx * h, fy - rh * 0.35, rh * 0.42, rh * 0.7, PAL.navy, { seed: (o.seed ?? 5) + 3, depth: 0.8 }); paperCircle(R_.dx * h, fy - rh * 0.8, rh * 0.16, PAL.pink, { seed: (o.seed ?? 5) + 4 }); }
      g.restore();
    }
    return Coaster.drawCart(tr, vw, s, { h, glow: o.glow || 0, seed: o.seed });
  }
  const RIDE_SCALE = 0.8;        // Max in the rap's wide shots: 0.8 × his video size (the formula cards sit above the crest)

  /* ---------------- act: ride (intro, verse 1, chorus 1) ---------------- */
  const KR = 22;
  function rideState(p, t) {
    if (t < p.tRide0) return p.ride.lift.sample(Math.max(0, t));
    return p.ride.sim.sample(t - p.tRide0);
  }
  /* camera: the mean cart x over the last 0.7 s (pure: only samples the past), never left of the lift */
  function rideCamX(p, t) {
    let acc = 0, ws = 0;
    for (let i = 0; i < 9; i++) { const w = 9 - i; acc += Math.max(30, rideState(p, Math.max(0, t - 0.7 * i / 8)).x) * w; ws += w; }
    return Math.min(290, acc / ws);
  }
  function actRide(S, p, t) {
    const st = rideState(p, t), R = p.ride, tr = R.track;
    const cx = rideCamX(p, t), vw = Coaster.view(960 - cx * KR, GROUND, KR);
    sky(cx * KR); groundStrip(cx * KR);
    const xl = cx - 960 / KR - 8, xr = cx + 960 / KR + 8;
    Coaster.drawTrack(tr, vw, { seed: 3, postEvery: 3.2, s0: tr.sAtX(Math.max(0, xl)), s1: tr.sAtX(Math.min(330, xr)) });
    if (xl < 8) {
      // the station sign sits just behind the start of the track, so the waiting cart doesn't cover it
      paperRect(vw.X(-4), GROUND - 40, 170, 80, PAL.red, { seed: 31, depth: 1, boil: false });
      label('station', vw.X(-4), GROUND - 40, 34, -1e6, { dur: 0.01, seed: 32 });
    }
    const lift = t < p.tRide0;
    if (xl < 36) Coaster.drawRuler(vw, 34, { y0: 0, y1: 20, mark: lift ? st.y : null, side: 'right', seed: 33 });
    const pose = rideCart(tr, vw, st.s, { h: 54, rider: 'max_cross', scale: RIDE_SCALE, seed: 5 });
    if (!lift && st.speed > 13) speedLines(pose.x - 20, pose.y - 10, Math.cos(st.angle), -Math.sin(st.angle), 70, 40, 3, 90 + BF % 3, 0.6);
    // the chain: "clack" marks on the beat while it pulls
    if (lift && st.x < 28.9 && st.x > 1.1) {
      const b = Math.floor(S.beatPos(t)), pu = S.pulse(t, 5);
      hand('clack!', pose.x - 80 - (b % 2) * 40, pose.y + 50 + (b % 2) * 20, 30 + 8 * pu, -1e6, { dur: 0.01, color: PAL.cream, outline: OUTLINE, alpha: 0.35 + 0.65 * pu, seed: 34 + (b % 2), rot: -0.35 });
    }
    // chorus 1: what the cart holds right now (honest: from the sample)
    if (t > S.S(10) - 0.3 && t < p.bRace) {
      const hi = st.PE / (st.PE + st.KE + 1e-9);
      const txt = hi > 0.6 ? 'mostly PE' : hi < 0.4 ? 'mostly KE' : 'swapping…';
      tag(txt, pose.x, pose.y - 120, 34, 1, { color: hi > 0.6 ? PAL.mustard : hi < 0.4 ? PAL.red : PAL.white, ink: hi < 0.4 ? PAL.cream : PAL.ink, seed: 35 });
    }
    // HUD: energy chart (left) + speed gauge (right); the chart pulses on downbeats after the intro
    const dp = t > S.S(2) ? S.downPulse(t) : 0, tc = S.W(13, 'conservation');
    Coaster.drawChart(36, 150, 330, 330, st, { ref: 100000, max: 125000, show: { heat: false }, s: 1 + 0.03 * dp,
      circleTotal: t > tc && t < p.bRace ? P(t, tc, tc + 0.5) : 0 });
    Coaster.drawGauge(1790, 250, 80, st.v, { max: 30 });
    // intro title
    if (t < S.S(2) + 0.6) {
      const ex = S.S(2);
      ransom('ENERGY', 980, 210, 108, 0.05, { seed: 101, exitT: ex });
      ransom("DON'T QUIT", 980, 340, 108, 0.45, { seed: 102, exitT: ex + 0.1 });
      if (t < ex) label('a rap about roller coasters', 960, 470, 50, 1.0, { seed: 103 });
    }
    if (t > S.W(1, 'Twenty') && t < S.S(4)) tag('20 m up', vw.X(27.7), vw.Y(20) - 80, 40, pop(t, S.W(1, 'Twenty')), { color: PAL.mustard, seed: 104 });
    // PE = m g h: each letter pops on its word
    const tM = S.W(4, 'Mass');
    formulaCard([{ s: 'PE =', t0: tM }, { s: 'm', t0: tM }, { s: 'g', t0: S.W(4, 'g') }, { s: 'h', t0: S.W(4, 'height') }], 960, 170, 58, tM - 0.05, { exitT: S.S(6) + 0.3, seed: 111 });   // high + small: clears the head of Max parked on the crest
    if (t > S.W(5, 'battery') - 0.1 && t < S.S(6) + 0.8) {
      // "like a battery full of cash": a paper battery above the crest, as full as PE / 100 kJ
      const bx = vw.X(45), by = vw.Y(20) + 10, sc = pop(t, S.W(5, 'battery'), 0.3) * unpop(t, S.S(6) + 0.5);
      if (sc > 0) {
        g.save(); g.translate(bx, by); g.rotate(-0.08); g.scale(sc, sc);
        paperRect(0, 0, 130, 62, PAL.white, { seed: 121, depth: 1, outline: true });
        paperRect(72, 0, 14, 26, PAL.ink, { seed: 122, depth: 0 });
        paperRect(-60 + 55 * clamp(st.PE / 100000), 0, 112 * clamp(st.PE / 100000), 46, PAL.green, { seed: 123, depth: 0, boil: false });
        g.restore();
        label('100 kJ', bx, by - 62, 36, S.W(5, 'full'), { seed: 124 });
      }
    }
    // KE = ½ m v²
    const tK = S.W(8, 'One');
    formulaCard([{ s: 'KE =', t0: tK }, { s: '½', t0: S.W(8, 'half') }, { s: 'm', t0: S.W(8, 'm') }, { s: 'v²', t0: S.W(8, 'v') }], 960, 190, 66, tK - 0.05, { exitT: S.S(10) - 0.1, seed: 131 });
    if (t > S.W(9, 'Double') && t < S.S(10)) {
      tag('speed ×2', 780, 320, 40, pop(t, S.W(9, 'Double')), { color: PAL.sky, seed: 132 });
      if (t > S.W(9, 'quadruple')) { label('→', 960, 320, 64, -1e6, { dur: 0.01, seed: 134 }); tag('KE ×4', 1130, 320, 40, pop(t, S.W(9, 'quadruple')), { color: PAL.red, ink: PAL.cream, seed: 133 }); }
    }
    // chorus 1: PE ⇄ KE, then TOTAL: same
    if (t > S.S(11) - 0.2 && t < p.bRace + WIPE) {
      formulaCard([{ s: 'PE', t0: S.W(11, 'potential') }, { s: '⇄', t0: S.W(11, 'down') }, { s: 'KE', t0: S.W(11, 'kinetic') }], 960, 190, 60, S.S(11) - 0.2, { seed: 141, s: 1 + 0.04 * dp });
      if (t > S.W(13, 'Total')) tag('TOTAL stays the same', 960, 300, 40, pop(t, S.W(13, 'Total')), { color: PAL.teal, ink: PAL.cream, seed: 142 });
    }
  }

  /* ---------------- act: race (the elephant bars) ---------------- */
  const KRACE = 14.5, LANE_OX = [70, 1010];        // two lanes side by side, same ground, same scale
  function actRace(S, p, t) {
    const R = p.race, tr = R.track, tau = clamp(t - p.tRace0, 0, 9);
    sky(0); groundStrip(0);
    const lanes = LANE_OX.map(ox => Coaster.view(ox, GROUND, KRACE));
    lanes.forEach((v, i) => {
      Coaster.drawTrack(tr, v, { seed: 3 + i, postEvery: 3.5 });
      const X = v.X(18), Y = v.Y(0);        // finish line at the bottom of the drop
      paperRect(X, Y - 70, 8, 140, PAL.white, { seed: 320 + i, depth: 0.6, boil: false });
      for (let a = 0; a < 4; a++) for (let b = 0; b < 3; b++) { g.fillStyle = (a + b) % 2 ? PAL.ink : PAL.white; g.fillRect(X + 4 + a * 14, Y - 140 + b * 14, 14, 14); }
    });
    const sm = R.sims.max.sample(tau), se = R.sims.elephant.sample(tau);
    const tMax = S.W(14, 'boy'), tEle = S.W(15, 'elephant');
    const pm = rideCart(tr, lanes[0], sm.s, { h: 54, rider: t > tMax ? 'max_cross' : null, scale: 0.9 * clamp(pop(t, tMax, 0.4), 0, 1.3), seed: 6 });
    const sq = 1 + 0.08 * Math.max(0, spring(t, tEle, 2.5, 4));
    const pe = rideCart(tr, lanes[1], se.s, { h: 54, rider: t > tEle ? 'elephant' : null, scale: sq * clamp(pop(t, tEle, 0.4), 0, 1.3), seed: 7 });
    if (t > tMax) tag(R.sims.max.m + ' kg', pm.x + 125, pm.y - 50, 36, pop(t, tMax + 0.25), { color: PAL.sky, seed: 510 });
    if (t > tEle) tag(R.sims.elephant.m.toLocaleString('en-US') + ' kg', pe.x + 150, pe.y - 50, 36, pop(t, tEle + 0.3), { color: PAL.pink, seed: 511 });
    if (t < p.tRace0 + 0.5) label('heavier = faster?', 960, 250, 64, S.W(14, 'heavy'), { seed: 512, rot: -0.03 });
    // the tie (times from the sims)
    if (t > p.tTie) {
      g.save(); g.globalAlpha = 0.8 * (1 - P(t, p.tTie, p.tTie + 0.4)); g.fillStyle = '#fffdf2'; g.fillRect(0, 0, 1920, 1080); g.restore();
      if (t < S.W(19, 'mass')) UI.stamp('TIE!', 640, 430, { s: 1.1 * pop(t, p.tTie + 0.05, 0.3), rot: -0.1 });
      const tm = R.sims.max.timeAtX(R.marks.bottom), te = R.sims.elephant.timeAtX(R.marks.bottom);
      if (t < S.S(17) - 0.15) label(`50 kg: ${tm.toFixed(2)} s   ·   5,000 kg: ${te.toFixed(2)} s`, 960, 250, 50, p.tTie + 0.3, { seed: 513, dur: 0.5 });
      const vb = R.sims.max.sample(tm).speed;
      if (t > S.W(18, 'speed')) {
        tag(vb.toFixed(1) + ' m/s', pm.x, pm.y - 175, 36, pop(t, S.W(18, 'speed')), { color: PAL.white, seed: 514 });
        tag(vb.toFixed(1) + ' m/s', pe.x, pe.y - 165, 36, pop(t, S.W(18, 'speed') + 0.1), { color: PAL.white, seed: 515 });
      }
    }
    // m g h = ½ m v², the m's crossed off ("cross it off"), then v = √(2 g h)
    const t17 = S.S(17), tR = S.W(18, 'Root'), FX = 960, FY = 190;
    const f1 = formulaCard([{ s: 'm', t0: t17 }, { s: 'g h', t0: t17 + 0.1 }, { s: '=', t0: t17 + 0.2 }, { s: '½', t0: t17 + 0.3 }, { s: 'm', t0: t17 + 0.35 }, { s: 'v²', t0: t17 + 0.4 }],
      FX, FY, 64, t17 - 0.1, { exitT: tR - 0.15, seed: 151 });
    if (f1 && t > S.W(17, 'cross')) {
      [0, 4].forEach((j, n) => {
        const tk = f1.toks[j], t0 = S.W(17, n ? 'off' : 'cross'), X = FX + tk.x * f1.sc, hw = 30 * f1.sc;
        strike(X - hw, FY + 34 * f1.sc, X + hw, FY - 34 * f1.sc, P(t, t0, t0 + 0.25), 160 + n);
      });
    }
    formulaCard([{ s: 'v =', t0: tR }, { s: '√(2 g h)', t0: S.W(18, 'two') }], FX, FY, 72, tR - 0.1, { seed: 171 });
    if (t > S.W(18, 'speed')) label('= √(2 · 10 · 20) = 20 m/s', FX, FY + 112, 46, S.W(18, 'speed'), { seed: 172 });
    if (t > S.W(19, 'mass')) UI.stamp('NO MASS!', 640, 430, { s: 1.0 * pop(t, S.W(19, 'mass'), 0.3), rot: 0.08 });
  }

  /* ---------------- act: friction ---------------- */
  const KF = 15, FRIC_OX = 430;
  function actFriction(S, p, t) {
    const R = p.fric, tr = R.track, sim = R.sim, tau = Math.max(0, t - p.tFric0);
    const st = sim.sample(tau), vw = Coaster.view(FRIC_OX, GROUND, KF);
    sky(300); groundStrip(300);
    Coaster.drawTrack(tr, vw, { seed: 8, postEvery: 3.2 });
    paperRect(vw.X(85), GROUND - 12, 30 * KF, 12, PAL.red, { seed: 41, depth: 0.4, boil: false, alpha: 0.8 });
    label('brakes', vw.X(85), GROUND - 44, 32, -1e6, { dur: 0.01, seed: 42 });
    // warm wheels: glow from the friction power (heat rate, W) — honest, from the sim
    const h0 = sim.sample(Math.max(0, tau - 0.25)).heat, rate = (st.heat - h0) / 0.25;
    const glow = clamp(rate / 9000);
    const pose = rideCart(tr, vw, st.s, { h: 54, rider: 'max_cross', scale: RIDE_SCALE, glow, seed: 5 });
    if (st.speed > 13) speedLines(pose.x - 20, pose.y - 10, Math.cos(st.angle), -Math.sin(st.angle), 60, 36, 3, 90 + BF % 3, 0.5);
    Coaster.drawChart(36, 150, 360, 330, st, { max: st.total0 * 1.25, s: 1 + 0.03 * S.downPulse(t) });
    if (t > S.W(20, 'bites') && t < S.S(22)) label('friction takes a bite → heat', 216, 520, 36, S.W(20, 'bites'), { seed: 43 });
    // "the first hill is always the tallest": crest heights (from the track), then the falling line
    const crests = R.marks.crests, hs = crests.map(x => tr.at(tr.sAtX(x)).y);
    const tFirst = S.W(22, 'first'), tLow = S.W(23, 'lower');
    crests.forEach((x, i) => {
      const t0 = i === 0 ? tFirst : S.W(22, 'tallest') + (i - 1) * 0.35;
      if (t > t0) tag(Math.round(hs[i]) + ' m', vw.X(x), vw.Y(hs[i]) - 96, 38, pop(t, t0, 0.3), { color: i === 0 ? PAL.mustard : PAL.cream, seed: 440 + i, rot: i % 2 ? 0.05 : -0.05 });
    });
    if (t > tLow) {
      const pts = crests.map((x, i) => [vw.X(x), vw.Y(hs[i]) - 150]);
      ink(pts, { w: 6, color: PAL.red, seed: 450, prog: P(t, tLow, tLow + 0.8), passes: 2, step: 12 });
      if (t > tLow + 0.6) label('every hill lower', vw.X(56), vw.Y(11) - 250, 48, tLow + 0.6, { seed: 451, rot: 0.08 });
    }
  }

  /* ---------------- act: finale (chorus 2 + outro) ---------------- */
  const KL = 17, LOOP_OX = 560;
  function actFinale(S, p, t) {
    const R = p.loop, tr = R.track, sim = R.sim;
    const k = Math.floor((t - p.tLoop0) / p.loopCycle), tau = t < p.tLoop0 ? 0 : (t - p.tLoop0) - k * p.loopCycle;
    const st = sim.sample(tau), vw = Coaster.view(LOOP_OX, GROUND, KL);
    sky(600); groundStrip(600);
    Coaster.drawTrack(tr, vw, { seed: 11, postEvery: 3 });
    const pose = rideCart(tr, vw, st.s, { h: 52, rider: 'max_cross', scale: RIDE_SCALE, seed: 5 });
    if (st.speed > 12) speedLines(pose.x - 20, pose.y - 10, Math.cos(st.angle), -Math.sin(st.angle), 60, 36, 3, 90 + BF % 3, 0.6);
    // Voss and Max nod on every beat (bob/squash from a beat envelope) and sway bar by bar
    const pu = S.pulse(t, 7), bar = Math.floor(S.beatPos(t) / 4), sway = (bar % 2 ? 1 : -1) * 0.03;
    const hot = (i, w) => t > S.W(i, w) && t < S.W(i, w) + 1.0;
    const point = hot(25, 'potential') || hot(25, 'kinetic') || hot(27, 'conservation') || t > S.S(30);
    Puppet.draw('max', 190, 935, 480, { env: 0.2 + 0.8 * pu, facing: 1, t, pose: point ? 'point' : 'cross', rot: sway });
    Puppet.draw('voss', 1735, 935, 510, { env: 0.2 + 0.8 * pu, facing: -1, t, pose: point ? 'point' : 'clip', rot: -sway });
    const dp = S.downPulse(t), tc = S.W(27, 'conservation');
    Coaster.drawChart(40, 150, 300, 290, st, { show: { heat: false }, s: 1 + 0.05 * dp, max: st.total0 * 1.3,
      circleTotal: t > tc && t < tc + 3 ? P(t, tc, tc + 0.5) : 0 });
    // top: the chorus card, then the title chant, then CLASS DISMISSED
    const tOut = S.S(28);
    if (t < tOut) {
      formulaCard([{ s: 'PE', t0: S.W(25, 'potential') }, { s: '⇄', t0: S.W(25, 'down') }, { s: 'KE', t0: S.W(25, 'kinetic') }], 960, 190, 60, S.S(24) + 0.2, { seed: 181, s: 1 + 0.05 * dp, exitT: tOut - 0.3 });
      if (t > S.W(27, 'Total')) tag('TOTAL stays the same', 960, 300, 40, pop(t, S.W(27, 'Total')) * unpop(t, tOut - 0.3), { color: PAL.teal, ink: PAL.cream, seed: 182 });
    } else if (t < S.S(30)) {
      const s = 1 + 0.06 * dp;
      g.save(); g.translate(1070, 230); g.scale(s, s);   // right of the crest, so it clears Max parked on the loop track's start
      ransom('ENERGY', 0, -70, 96, tOut, { seed: 191, exitT: S.S(30) - 0.5 });
      ransom("DON'T QUIT", 0, 60, 96, tOut + 0.3, { seed: 192, exitT: S.S(30) - 0.4 });
      g.restore();
    }
    if (t > S.S(30)) {
      UI.stamp('CLASS DISMISSED', 960, 250, { s: 1.2 * pop(t, S.S(30), 0.35), rot: -0.06 });
    }
  }

  /* torn-paper wipe: the old act torn away right → left, revealing the new one */
  function wipe(drawOld, drawNew, t, t0, dur, seed) {
    const p = E.inOutCubic(P(t, t0, t0 + dur));
    drawNew();
    if (p >= 1) return;
    const edge = tornEdgePts(lerp(1920 + 220, -260, p), seed);
    const poly = [[-100, -100], ...edge, [-100, 1180]];
    g.save(); shadowFill(poly, 2.2, PAL.paper); g.restore();          // the sheet's shadow: pre-baked path, no per-frame blur
    g.save(); pathPts(poly); g.clip(); drawOld(); g.restore();
    const r = rng(seed + 1), inner = edge.map(q => [q[0] - 10 - r() * 14, q[1]]);
    g.save(); pathPts([...edge, ...inner.reverse()]); g.fillStyle = paperTile(PAL.white, 2); g.fill(); g.restore();
  }
  const ACTS = [actRide, actRace, actFriction, actFinale];
  function actAt(p, t) { const b = [0, p.bRace, p.bFric, p.bFinale]; let k = 0; for (let i = 0; i < 4; i++) if (t >= b[i]) k = i; return [k, b[k]]; }
  function drawActs(song, p, t) {
    const [k, b] = actAt(p, t);
    const run = i => () => { g.save(); ACTS[i](song, p, t); g.restore(); shadowOff(); g.globalAlpha = 1; };
    if (k > 0 && t < b + WIPE) wipe(run(k - 1), run(k), t, b, WIPE, 5 + k);
    else run(k)();
  }

  /* ======================================================================
     the scene
     ====================================================================== */
  class RapScene extends Scene {
    enter() {
      this.take = TAKE;
      this.song = null; this.plan = null; this.err = null;
      this.playing = false; this.frozen = false; this.ended = false; this.startedOnce = false;
      this.pos = 0;                    // song time while paused / on the wall clock
      this.t0ctx = null;               // Audio.now() at song time 0 while the audio clock drives
      this.heard = 0;                  // seconds actually played (for "first full listen")
      this.audioReady = false; this.completed = false; this.gone = false;
      Audio.stopMusic();
      const key = 'rap:' + this.take;
      // through the loader when present: the rap tier may already have fetched it (core/loader.js)
      const wurl = Assets.url(DIR + this.take + '_words.json');
      const words = (window.Loader ? Loader.json(wurl).then(d => d || Promise.reject(new Error('no timings'))) : fetch(wurl).then(r => (r.ok ? r.json() : Promise.reject(new Error('HTTP ' + r.status)))))
        .then(d => { this.song = new Song(d); this.plan = plan(this.song); })
        .catch(e => { this.err = String(e && e.message); console.warn('[rap] no timings for ' + this.take, this.err); });
      const audio = (Audio.has(key) ? Promise.resolve() : Audio.load({ [key]: Assets.url(DIR + this.take + '.m4a') })).then(() => { this.audioReady = Audio.has(key); });
      // autoplay once both are in (the click on the map tag unlocked audio); QA may have frozen a frame first
      Promise.all([words, audio]).then(() => { if (!this.gone && !this.frozen && !this.startedOnce && this.song) this.play(0); });
      // a tab switch: pause (core/device.js pauses the element and shows its PAUSED card); that card's tap resumes
      if (window.Device) {
        this.onHidden = Device.on('hidden', () => { if (this.playing && !this.gone) { this.pause(); this.resumeOnReturn = true; } });
        this.onResume = Device.on('resume', () => { if (this.resumeOnReturn && !this.gone && Game.current() === this) { this.resumeOnReturn = false; this.play(this.pos); } });
      }
    }
    exit() {
      this.gone = true; Audio.stopGroup('rap'); this.playing = false;
      if (this.media) this.media.pause();
      if (window.Device) { Device.off('hidden', this.onHidden); Device.off('resume', this.onResume); }
    }

    get key() { return 'rap:' + this.take; }
    /* the song clock: the <audio> element while it plays (t0ctx != null = "on the audio clock"), else pos */
    now() {
      if (this.playing && this.t0ctx != null) {
        const m = this.media;
        if (m) return m.playing() ? m.time() : m.el.ended ? this.song.dur : this.pos;
        if (Audio.running()) return Audio.now() - this.t0ctx;
      }
      return this.pos;
    }
    play(from = this.now()) {
      if (!this.song) return;
      this.startedOnce = true; this.frozen = false; this.ended = false;
      from = clamp(from, 0, this.song.dur - 0.05);
      Audio.stopGroup('rap');
      this.pos = from; this.playing = true; this.t0ctx = null;
      if (!this.audioReady || window.__timeScale > 1) return;
      this.media = this.media || Audio.media(this.key);
      if (this.media) {
        // long music: the <audio> element is the clock; until it actually runs, the wall clock carries on
        const m = this.media, tok = this.playTok = (this.playTok || 0) + 1;
        this.playAt = Game.time; this.blocked = false;
        m.play(from).then(ok => {
          if (tok !== this.playTok || this.gone) return;
          if (ok && this.playing) { this.pos = m.time(); this.t0ctx = 0; }
          else if (!ok) { this.pause(); this.blocked = true; }      // the browser wants a tap first: show "tap to play"
        });
      } else if (Audio.running()) {         // a decoded buffer (streams unsupported): the Web Audio clock
        const at = Audio.now() + 0.06;
        Audio.schedule(this.key, { at, offset: from, gain: 1, group: 'rap', bus: 'voice', fade: 0.03 });
        this.t0ctx = at - from;
      }
    }
    pause() {
      this.pos = this.now(); this.playing = false; this.t0ctx = null; this.playTok = (this.playTok || 0) + 1;
      Audio.stopGroup('rap');
      if (this.media) this.media.pause();
    }
    toggle() { if (this.playing) this.pause(); else this.play(this.ended ? 0 : this.pos); Audio.play('paper', { gain: 0.25 }); }
    seek(t) { if (!this.song) return; const was = this.playing; this.pause(); this.pos = clamp(t, 0, this.song.dur); this.ended = false; if (was) this.play(this.pos); }
    restart() { this.heard = 0; this.play(0); }
    /* QA: freeze on song time t (no pause overlay) */
    debugAt(t) { this.pause(); this.frozen = true; this.startedOnce = true; this.ended = false; this.pos = t; }

    update(dt) {
      if (!this.song || !this.playing || this.scrub) return;
      if (this.t0ctx == null) {
        // wall clock (audio locked, missing or still starting); hop onto the audio clock as soon as it runs
        this.pos += dt * (window.__timeScale || 1);
        const retry = this.audioReady && !(window.__timeScale > 1) &&
          (this.media ? !this.blocked && Game.time - (this.playAt || 0) > 3 : Audio.running());
        if (retry) this.play(this.pos);
      }
      this.heard += dt * (this.t0ctx == null ? (window.__timeScale || 1) : 1);
      if (this.now() >= this.song.dur - 0.05) {
        this.pause(); this.pos = this.song.dur; this.ended = true;
        if (!this.completed && this.heard > 0.85 * this.song.dur) {
          this.completed = true;
          if (!Game.progress.stamps.rap) Game.complete('rap');
        }
      }
    }
    keyDown(k) {
      if (!this.song) return;
      if (k === ' ') this.toggle();
      else if (k === 'r' || k === 'R') this.restart();
      else if (k === 'ArrowRight') this.seek(this.now() + 5);
      else if (k === 'ArrowLeft') this.seek(this.now() - 5);
      else if (k === 'Enter' && this.ended) Game.replace(new Levels.map());
    }
    /* the HUD buttons (⌂ ↺ ▶), right to left; at scale 1: r 40 at x 1850 / 1750 / 1650, y 70 */
    buttons() {
      const r = UI.tap(80) / 2;
      return { r, y: Math.max(70, r + 10), x: [0, 1, 2].map(i => 1890 - r - i * (2 * r + 20)) };
    }
    /* the progress strip's hit zone height (32 px; ≥ 44 CSS px on a touch screen) */
    stripZone() { return Math.max(32, UI.tap(32)); }
    pointerDown(x, y) {
      if (!this.song || this.ended) return;
      if (UI.docked()) { this.toggle(); return; }                        // portrait dock: the stage is one big play / pause
      const B = this.buttons();
      if (y < B.y + B.r + 10 && x > B.x[2] - B.r - 10) return;          // the buttons
      if (y > 1080 - this.stripZone()) {                                 // the strip: press = seek, drag = scrub
        this.scrub = { was: this.playing };
        if (this.playing) this.pause();
        this.pos = clamp(x / 1920 * this.song.dur, 0, this.song.dur - 0.05); this.ended = false;
        return;
      }
      this.toggle();
    }
    pointerMove(x) { if (this.scrub && this.song) this.pos = clamp(x / 1920 * this.song.dur, 0, this.song.dur - 0.05); }
    pointerUp() {
      if (!this.scrub) return;
      const was = this.scrub.was; this.scrub = null;
      if (was) this.play(this.pos);                                      // inside the gesture: <audio> may play
    }

    draw() {
      const docked = UI.docked();
      if (!this.song) {
        g.fillStyle = paperTile(PAL.kraft, 3, 'tex_kraft'); g.fillRect(0, 0, 1920, 1080);
        ransom("ENERGY DON'T QUIT", 960, 420, 90, this.enteredAt + 0.2, { seed: 42 });
        const msg = this.err ? 'the song timings are missing (' + this.err + ')' : 'warming up the band…';
        hand(msg, 960, 600, 54, this.enteredAt + 0.4, { color: PAL.navy, seed: 44 });
        if (docked) { Dock.head("Energy Don't Quit", ''); Dock.text('rap-wait', [msg], { cls: 'note center' }); this.dockBar(); }
        else this.chrome(null);
        return;
      }
      const t = clamp(this.now(), 0, this.song.dur), gT = T, gBF = BF;
      T = t; BF = Math.floor(t * 8);
      try { drawActs(this.song, this.plan, t); if (!docked) band(this.song, t); }
      finally { T = gT; BF = gBF; }
      if (docked) this.dock(t); else this.chrome(t);
    }

    /* ---------- the portrait dock (core/dock.js): karaoke, controls and the strip below the stage ---------- */
    dock(t) {
      const S = this.song, pl = this.plan;
      const mmss = s => Math.floor(s / 60) + ':' + String(Math.floor(s % 60)).padStart(2, '0');
      Dock.head("Energy Don't Quit", `${mmss(t)} / ${mmss(S.dur)}`);
      const { cur, next } = lyricAt(S, t);
      const sec = (cur || next || {}).section;
      const lastE = S.items.length ? S.items[S.items.length - 1].e : 0;
      if (cur) {
        const key = cur.i + ':' + cur.s;
        if (!this._dk || this._dk.key !== key) {
          const hook = w => HOOK.has(normW(String(w).replace(/[,.!?]+$/, '')));        // the hook words, as on the stage
          this._dk = { key, words: cur.words.map(w => ({ w: w.w, s: w.s, e: w.e, hook: hook(w.w) })) };
        }
        Dock.karaoke({ key, words: this._dk.words, t, next: next ? next.text : '', section: sec ? sec.toLowerCase() : '' });
      } else Dock.karaoke({ key: 'gap', words: [], t, next: next ? next.text : '', section: sec ? sec.toLowerCase() : '',
        note: t > lastE && S.items.length ? 'the end ♪' : this.startedOnce || this.frozen ? '♪ ♫ ♪' : 'loading the song…' });
      Dock.text('credit', ['♪ AI-generated song (take ' + TAKE_NAME + ') · the physics is real'], { cls: 'small center' });
      if (this.ended && Game.current() === this) {
        Dock.buttons('rap-end', [{ label: '↺ Play it again', color: PAL.teal, onClick: () => this.restart() },
          { label: 'Back to the map', color: PAL.red, onClick: () => Game.replace(new Levels.map()) }]);
      }
      Dock.strip({ value: t, total: S.dur, ticks: [pl.bRace, pl.bFric, pl.bFinale], label: 'song position', onSeek: (v, phase) => this.scrubTo(v, phase) });
      this.dockBar();
      // the stage: a paper ▶ while paused (the words and buttons are in the dock)
      if (!this.playing && !this.frozen && this.startedOnce && !this.ended && !this.scrub) {
        g.save(); g.fillStyle = 'rgba(27,21,18,0.28)'; g.fillRect(0, 0, 1920, 1080); g.restore();
        paperCircle(960, 540, 150, PAL.mustard, { seed: 63, depth: 1.6 });
        g.save(); g.fillStyle = PAL.ink; g.beginPath(); g.moveTo(912, 458); g.lineTo(1035, 540); g.lineTo(912, 622); g.closePath(); g.fill(); g.restore();
      }
    }
    dockBar() {
      if (this.song && !this.ended) {
        Dock.button({ id: 'restart', glyph: '↺', label: 'restart', aria: 'play the song from the start', onClick: () => this.restart() });
        Dock.button({ id: 'play', glyph: this.playing ? '❚❚' : '▶', label: this.playing ? 'pause' : 'play', aria: this.playing ? 'pause' : 'play', primary: true, onClick: () => this.toggle() });
      }
      Dock.button({ id: 'map', glyph: '⌂', label: 'map', aria: 'back to the map', onClick: () => Game.replace(new Levels.map()) });
    }
    /* the dock's strip: press = seek, drag = scrub, release resumes (inside the gesture: the <audio> may play) */
    scrubTo(v, phase) {
      if (!this.song) return;
      const pos = clamp(v, 0, this.song.dur - 0.05);
      if (phase === 'start') { this.scrub = { was: this.playing || (this.scrub && this.scrub.was) }; if (this.playing) this.pause(); this.ended = false; }
      this.pos = pos;
      if (phase === 'end') { const was = this.scrub ? this.scrub.was : false; this.scrub = null; if (was) this.play(this.pos); }
    }

    chrome(t) {
      const t0 = this.enteredAt;
      ransom("ENERGY DON'T QUIT", 40, 56, 32, t0 + 0.1, { align: 'left', seed: 61 });
      hand('♪ AI-generated song · take ' + TAKE_NAME, 44, 108, 26, t0 + 0.4, { align: 'left', color: PAL.cream, outline: OUTLINE, seed: 62 });
      const B = this.buttons(), fx = { r: B.r, fixed: true };
      if (UI.iconButton('rap-map', '⌂', B.x[0], B.y, fx)) { Game.replace(new Levels.map()); return; }
      if (this.song) {
        if (UI.iconButton('rap-restart', '↺', B.x[1], B.y, fx)) this.restart();
        if (UI.iconButton('rap-play', this.playing ? '❚❚' : '▶', B.x[2], B.y, { ...fx, color: PAL.mustard })) this.toggle();
      }
      if (t == null) return;
      const S = this.song, pl = this.plan, zone = this.stripZone(), touch = zone > 32;
      if (!this.ended) UI.target('rap-strip', 960, 1080 - zone / 2, 1920, zone);
      // the strip: 12 px; on a touch screen a thicker one with a knob, so it reads as draggable
      const sh = touch ? 22 : 12, sy = 1080 - sh;
      g.save();
      g.fillStyle = 'rgba(43,36,32,0.25)'; g.fillRect(0, sy, 1920, sh);
      g.fillStyle = PAL.red; g.fillRect(0, sy, 1920 * t / S.dur, sh);
      g.fillStyle = PAL.navy; [pl.bRace, pl.bFric, pl.bFinale].forEach(b => g.fillRect(1920 * b / S.dur - 2, sy - 6, 4, sh + 6));
      g.restore();
      if (touch || this.scrub) paperCircle(clamp(1920 * t / S.dur, 26, 1894), touch ? 1080 - 24 : sy + sh / 2, touch ? 22 : 14, PAL.navy, { seed: 66, depth: 0.8, boil: false });
      if (!this.playing && !this.frozen && this.startedOnce && !this.ended) {
        g.save(); g.fillStyle = 'rgba(27,21,18,0.28)'; g.fillRect(0, 0, 1920, BAND_Y); g.restore();
        paperCircle(960, 430, 100, PAL.mustard, { seed: 63, depth: 1.6 });
        g.save(); g.fillStyle = PAL.ink; g.beginPath(); g.moveTo(928, 375); g.lineTo(1010, 430); g.lineTo(928, 485); g.closePath(); g.fill(); g.restore();
        hand(UI.hint('paused · click or Space to play', 'paused · tap to play'), 960, 590, 48, -1e6, { dur: 0.01, color: PAL.cream, outline: OUTLINE, seed: 64 });
      }
      if (!this.startedOnce && !this.frozen) hand('loading the song…', 960, 590, 48, this.enteredAt + 0.3, { color: PAL.cream, outline: OUTLINE, seed: 65 });
      if (this.ended && Game.current() === this) {
        if (UI.button('rap-again', '↺ Play it again', 700, 560, { w: 440, h: 110, color: PAL.teal, size: 56 })) this.restart();
        if (UI.button('rap-back', 'Back to the map', 1220, 560, { w: 440, h: 110, color: PAL.red, size: 56 })) Game.replace(new Levels.map());
      }
    }
  }
  RapScene.TAKE = TAKE;
  RapScene.Song = Song;
  RapScene.plan = plan;
  Levels.rap = RapScene;
})();
